import React, { useState } from "react";
import { Box, Button, Stack } from "@mui/joy";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import SelectCheckbox from "components/onboarding/SelectCheckBox";
import { BottomNextButton } from "components/common/BottomNextButton";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  COMMITMENT_DETAIL_ENDPOINT,
  COMMITMENT_DOMAIN,
  commitmentApi,
} from "api/commitmentApi";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import CustomModal from "../common/CustomModal";
import useClose from "../../hooks/useClose";

const options: { id: number; text: string }[] = [
  { id: 1, text: "변화를 믿고 시간을 투자하겠습니다." },
  { id: 2, text: "포기하지 않고 꾸준히 나아가겠습니다." },
  { id: 3, text: "완벽보다 작은 성취를 인정하겠습니다." },
  { id: 4, text: "스스로를 다그치기보다 격려하겠습니다." },
  { id: 5, text: "작은 어려움도 코치와 함께하겠습니다." },
];

export default function SelfCommitmentContent({
  isForWithdrawal = false,
}: {
  isForWithdrawal: boolean;
}) {
  const navigate = useNavigate();
  const user = useRecoilValue(userAtom);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const close = useClose();

  const { data: commitmentDetail, isLoading } = useQuery(
    [COMMITMENT_DOMAIN, COMMITMENT_DETAIL_ENDPOINT, user?.userId],
    () => commitmentApi.getDetail(),
    {
      staleTime: 1000 * 60,
    }
  );

  // data?.result?.signature may be a full data URL or base64 only
  const rawSig = (commitmentDetail as any)?.result?.signature as
    | string
    | undefined
    | null;
  const committedAt = (commitmentDetail as any)?.result?.committedAt as
    | string
    | undefined
    | null;

  const signatureSrc = React.useMemo(() => {
    if (!rawSig) return null;
    if (rawSig.startsWith("data:")) return rawSig;
    // assume it's png base64
    return `data:image/png;base64,${rawSig}`;
  }, [rawSig]);

  const displayDate = React.useMemo(() => {
    if (!committedAt) return null;
    const d = new Date(committedAt);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [committedAt]);

  // 자동 건너뛰기: 서명이 없으면 다음 페이지로 이동
  React.useEffect(() => {
    if (!isLoading && !signatureSrc) {
      navigate(isForWithdrawal ? `/refund?seq=15` : `/refund?seq=1`, { replace: true });
    }
  }, [isLoading, signatureSrc, isForWithdrawal, navigate]);

  const handleConfirmCancel = () => {
    setOpenCancelModal(false);
    close();
  };

  const body = (
    <Box sx={{ paddingBottom: "120px" }}>
      <Stack
        spacing={10}
        sx={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Stack spacing={1}>
          <Stack spacing={5}>
            <CustomTypography
              variant="Subtitle"
              fontWeight={500}
              sx={{ color: theme.vars.palette.Content.Strong }}
            >
              {!isForWithdrawal
                ? "프로그램을 시작하며 스스로와 맺은 약속을 다시 살펴보겠습니다."
                : "이제 나와의 약속을 파기하겠습니다."}
            </CustomTypography>
            {user?.nickname && (
              <CustomTypography
                variant="Body"
                fontWeight={500}
                sx={{ color: theme.vars.palette.Content.Medium }}
              >
                {`나 ${user?.nickname}${(() => {
                  const raw = user?.nickname || "";
                  const s = raw.trim();
                  if (!s) return "는";
                  const ch = s.charCodeAt(s.length - 1);
                  const isHangul = ch >= 0xac00 && ch <= 0xd7a3;
                  if (isHangul) {
                    const hasBatchim = ((ch - 0xac00) % 28) !== 0;
                    return hasBatchim ? "은" : "는";
                  }
                  const last = s[s.length - 1];
                  if ((/[A-Za-z]/).test(last)) {
                    return /[AEIOUaeiou]/.test(last) ? "는" : "은";
                  }
                  return "는";
                })()}...`}
              </CustomTypography>
            )}
          </Stack>
          <Stack spacing={0.5}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {options.map((option) => (
                <SelectCheckbox
                  key={option.id}
                  checked={true}
                  disabled={true}
                  onChange={() => {}}
                  label={option.text}
                />
              ))}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "16px",
                  backgroundColor: theme.vars.palette.Surface.Forward,
                  height: 200,
                  overflow: "hidden",
                }}
              >
                {!isLoading && !signatureSrc && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CustomTypography
                      variant="Body"
                      sx={{ color: theme.vars.palette.Content.SuperWeak }}
                    >
                      서명 정보가 없습니다
                    </CustomTypography>
                  </Box>
                )}

                {!!signatureSrc && (
                  <img
                    src={signatureSrc}
                    alt="commitment-signature"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                )}

                {!!displayDate && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 20,
                      bottom: 18,
                      pointerEvents: "none",
                    }}
                  >
                    <CustomTypography
                      variant="Caption"
                      sx={{
                        color: theme.vars.palette.Content.Weak,
                        fontWeight: 500,
                      }}
                    >
                      {displayDate}
                    </CustomTypography>
                  </Box>
                )}
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Stack>
      {!isForWithdrawal ? (
        <BottomNextButton
          disabled={false}
          loading={false}
          onClick={() => navigate(`/refund?seq=1`)}
          text={"다음"}
        />
      ) : (
        <Box
          sx={{
            position: "fixed",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: 0,
            width: "100%",
            maxWidth: "660px",
            zIndex: 1200,
            px: "20px",
            py: 2,
            display: "flex",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Button
            variant={"Outlined"}
            size={"md"}
            sx={{
              height: "54px",
              width: "50%",
              fontWeight: 700,
              backgroundColor: theme.vars.palette.Background.Level1,
            }}
            onClick={() => setOpenCancelModal(true)}
          >
            {"계속 지키기"}
          </Button>
          <Button
            variant={"Action"}
            color={"danger"}
            size={"md"}
            sx={{
              height: "54px",
              width: "50%",
              fontWeight: 700,
            }}
            onClick={() => navigate(`/refund?seq=15`)}
          >
            {"철회하기"}
          </Button>
        </Box>
      )}
      {/* Cancel Confirmation Modal */}
      <CustomModal
        open={openCancelModal}
        onClose={() => setOpenCancelModal(false)}
        title={"환불 신청을 취소할까요?"}
        subtitle={"앞으로 어떤 도움이 필요한지 코치 선생님과 이야기를 나눠보세요."}
        actions={[
          {
            label: "프로그램 계속 이용하기",
            onClick: handleConfirmCancel,
            variant: "Solid",
          },
        ]}
      />
    </Box>
  );

  return <>{body}</>;
}
